import type { ThemeContext } from '../themeContext';
import { clamp, easeOutCubic, stepSpring, type SpringState } from './math';
import type { FlowThemeProgress, FlowThemeScene, SceneAnchor, SceneFrame, SceneRect } from './types';

const LEAD_OMEGA = 5.4;
const POSITION_OMEGA = 3.4;
const FINALE_OMEGA = 2.1;
const INTRO_SECONDS = 1.8;
const STILL_TIME = 12;
// Still frames jump the scene clock this far past anything that happened while
// animating, so every timed effect reads as long finished rather than frozen.
const STILL_SETTLE_SECONDS = 60;
const STEP_TRIGGER = 0.2;
const FINALE_TRIGGER = 0.3;
/** Longest frame step the springs and scene clock take; longer gaps (pauses) count as this. */
export const MAX_FRAME_SECONDS = 1 / 20;

// Mirrors QuestionCard's standard width: min(41.25rem, 100vw - 3.25rem), centred.
const CARD_MAX_WIDTH = 660;
const CARD_GUTTER = 26;

const cardFootprint = (width: number, height: number, compact: boolean): SceneRect => {
  const half = Math.max(0, Math.min(CARD_MAX_WIDTH, width - CARD_GUTTER * 2) / 2);
  return {
    left: width / 2 - half,
    right: width / 2 + half,
    top: height * (compact ? 0.18 : 0.22),
    bottom: height * (compact ? 0.78 : 0.76),
  };
};

export interface DriverEvents {
  /** The lead reached a new step (fires as the step's visuals land). */
  onStep?: (anchor: SceneAnchor | null, frame: SceneFrame) => void;
  /** The flow finished and the finale is under way. */
  onFinale?: (anchor: SceneAnchor | null, frame: SceneFrame) => void;
}

/**
 * Owns the motion state between a flow's discrete progress and a scene:
 * a fast "lead" spring, a slower "position" spring cascaded behind it (which
 * gives camera moves an ease-in-out glide that survives rapid clicking), the
 * finale blend and the entrance. Framework-agnostic so previews and tests can
 * drive scenes without React.
 */
export class ThemeDriver {
  private readonly lead: SpringState = { x: 0, v: 0 };
  private readonly position: SpringState = { x: 0, v: 0 };
  private readonly finale: SpringState = { x: 0, v: 0 };
  private target: FlowThemeProgress = { step: 0, total: 1, complete: false };
  /**
   * Scene time in seconds. It advances by the clamped frame delta rather than
   * wall-clock time, so pauses (background tabs, idle previews) never make
   * timers jump forward, and it never runs backwards across still frames.
   */
  private clock = 0;
  private wasStill = false;
  private lastTime: number | null = null;
  private lastStepFired = 0;
  private finaleFired = false;
  private initialized = false;
  private latestFrame: SceneFrame | null = null;

  constructor(private readonly scene: FlowThemeScene, private readonly events: DriverEvents = {}) {}

  private themeContext?: ThemeContext;

  setThemeContext(value?: ThemeContext) { this.themeContext = value; }

  setTarget(next: FlowThemeProgress) {
    const total = Math.max(1, Math.round(next.total) || 1);
    const step = clamp(Math.round(next.step) || 0, 0, total);
    const complete = Boolean(next.complete);
    if (!this.initialized) {
      // Arrive where the learner already is; only later steps animate.
      this.lead.x = step;
      this.position.x = step;
      this.finale.x = complete ? 1 : 0;
      this.lastStepFired = step;
      this.finaleFired = complete;
      this.initialized = true;
    }
    if (step < this.lastStepFired) {
      this.lastStepFired = step;
    }
    if (!complete) {
      this.finaleFired = false;
    }
    this.target = { step, total, complete };
  }

  /**
   * Advances motion to `now` (seconds) and renders one frame. `maxDelta` caps
   * the step; a host drawing at a deliberately low frame rate raises it so
   * ambient motion keeps real-time speed.
   */
  render(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    compact: boolean,
    now: number,
    still = false,
    maxDelta = MAX_FRAME_SECONDS,
  ) {
    const dt = this.lastTime === null || still ? 0 : clamp(now - this.lastTime, 0, maxDelta);
    this.lastTime = now;
    if (still && !this.wasStill) {
      this.clock = this.clock === 0 ? STILL_TIME : this.clock + STILL_SETTLE_SECONDS;
    }
    this.wasStill = still;
    this.clock += dt;
    const { step, total, complete } = this.target;

    if (still) {
      this.lead.x = step;
      this.lead.v = 0;
      this.position.x = step;
      this.position.v = 0;
      this.finale.x = complete ? 1 : 0;
      this.finale.v = 0;
    } else {
      stepSpring(this.lead, step, LEAD_OMEGA, dt);
      stepSpring(this.position, this.lead.x, POSITION_OMEGA, dt);
      stepSpring(this.finale, complete ? 1 : 0, FINALE_OMEGA, dt);
    }

    const elapsed = this.clock;
    const frame: SceneFrame = {
      ctx,
      themeContext: this.themeContext,
      width,
      height,
      time: elapsed,
      dt: still ? 0 : dt,
      lead: this.lead.x,
      position: this.position.x,
      velocity: this.position.v,
      total: total || 1,
      progress: clamp(this.position.x / (total || 1)),
      finale: clamp(this.finale.x),
      intro: still ? 1 : easeOutCubic(clamp(elapsed / INTRO_SECONDS)),
      compact,
      still,
      card: cardFootprint(width, height, compact),
    };
    this.latestFrame = frame;

    ctx.save();
    try {
      this.scene.render(frame);
    } finally {
      // A scene that throws mid-draw must not leave its transform, clip or
      // alpha on the context for the next frame (or the host's fallback).
      ctx.restore();
    }

    if (!still) {
      while (this.lead.x >= this.lastStepFired + 1 - STEP_TRIGGER && this.lastStepFired < step) {
        this.lastStepFired += 1;
        this.scene.step?.(frame);
        // Reaching the end belongs to the finale, which has its own celebration.
        if (!(complete && this.lastStepFired >= total)) {
          this.events.onStep?.(this.scene.anchor(frame), frame);
        }
      }
      if (complete && !this.finaleFired && this.finale.x >= FINALE_TRIGGER) {
        this.finaleFired = true;
        this.events.onFinale?.(this.scene.anchor(frame), frame);
      }
    }
    return frame;
  }

  /** Whether springs are still moving (so a paused host knows to keep drawing). */
  isSettled() {
    const { step, complete } = this.target;
    return (
      Math.abs(this.lead.x - step) < 1e-3
      && Math.abs(this.position.x - step) < 1e-3
      && Math.abs(this.finale.x - (complete ? 1 : 0)) < 1e-3
      && Math.abs(this.position.v) < 1e-3
    );
  }

  /** Starts past the entrance, for a scene taking over from an identical one that just left. */
  skipIntro() {
    this.clock = Math.max(this.clock, INTRO_SECONDS);
  }

  anchor(): SceneAnchor | null {
    return this.latestFrame ? this.scene.anchor(this.latestFrame) : null;
  }

  dispose() {
    this.scene.dispose?.();
    this.latestFrame = null;
  }

  touch(x: number, y: number) {
    if (this.latestFrame) {
      this.scene.touch?.(x, y, this.latestFrame);
    }
  }
}
